import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import LoadingIcon from "../../assets/LoadingIcon";
import ConfirmDialog from "../../components/dialog/ConfirmDialog";
import {
  getSuperAdminTenant,
  getSuperAdminTenantFirebaseConfig,
  getSuperAdminTenantModules,
  updateSuperAdminTenantModule,
} from "../../services/superadmin-tenants";

const ADVANCED_PUSH_MODULE = "advanced_push_notifications";

const moduleDescriptions = {
  push_notifications:
    "Legacy synchronous topic notifications. This remains independent from advanced push.",
  advanced_push_notifications:
    "Installations, preferences, rich images, audiences, jobs, delivery status, and engagement.",
};

const TenantDetail = () => {
  const { tenantCode } = useParams();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modules, setModules] = useState([]);
  const [modulesLoading, setModulesLoading] = useState(true);
  const [moduleError, setModuleError] = useState(null);
  const [moduleSuccess, setModuleSuccess] = useState(null);
  const [pendingModules, setPendingModules] = useState({});
  const [firebaseConfig, setFirebaseConfig] = useState(null);
  const [confirmingDisable, setConfirmingDisable] = useState(null);

  useEffect(() => {
    const loadTenant = async () => {
      setLoading(true);
      setError(null);
      try {
        setTenant(await getSuperAdminTenant(tenantCode));
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };

    loadTenant();

    const loadModules = async () => {
      setModulesLoading(true);
      setModuleError(null);
      try {
        const response = await getSuperAdminTenantModules(tenantCode);
        setModules(response?.modules ?? []);
      } catch (e) {
        setModuleError(e.message);
      } finally {
        setModulesLoading(false);
      }
    };

    loadModules();

    const loadFirebaseReadiness = async () => {
      try {
        setFirebaseConfig(
          await getSuperAdminTenantFirebaseConfig(tenantCode)
        );
      } catch (e) {
        setFirebaseConfig(null);
      }
    };

    loadFirebaseReadiness();
  }, [tenantCode]);

  const updateModule = async (module) => {
    if (pendingModules[module.key]) {
      return;
    }

    const enabled = !module.enabled;
    setModuleError(null);
    setModuleSuccess(null);
    setConfirmingDisable(null);
    setPendingModules((current) => ({ ...current, [module.key]: true }));
    setModules((current) =>
      current.map((entry) =>
        entry.key === module.key ? { ...entry, enabled } : entry
      )
    );

    try {
      const updatedModule = await updateSuperAdminTenantModule(
        tenantCode,
        module.key,
        enabled
      );
      setModules((current) =>
        current.map((entry) =>
          entry.key === module.key ? updatedModule : entry
        )
      );
      setModuleSuccess(
        `${updatedModule.name} ${
          updatedModule.enabled ? "enabled" : "disabled"
        } successfully.`
      );
    } catch (e) {
      setModules((current) =>
        current.map((entry) =>
          entry.key === module.key ? module : entry
        )
      );
      setModuleError(e.message);
    } finally {
      setPendingModules((current) => ({ ...current, [module.key]: false }));
    }
  };

  const handleModuleToggle = (module) => {
    if (pendingModules[module.key]) {
      return;
    }
    if (module.key === ADVANCED_PUSH_MODULE && module.enabled) {
      setConfirmingDisable(module);
      return;
    }
    updateModule(module);
  };

  const firebaseReady =
    firebaseConfig?.active === true && firebaseConfig?.secretFilePresent === true;
  const advancedPushModule = modules.find(
    (module) => module.key === ADVANCED_PUSH_MODULE
  );

  if (loading) return <LoadingIcon />;

  if (error) {
    return (
      <div className="bg-errorBg text-errorText text-sm p-4 border-errorBorder border-[0.5px] rounded-sm">
        {error}
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between mb-6">
        <div>
          <h1 className="uppercase text-xl font-bold text-slate-700">
            {tenant.name}
          </h1>
          <p className="text-sm text-slate-500">{tenant.code}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link className="bg-white shadow px-4 py-2 rounded text-sm font-semibold" to={`/su/admin/tenants/${tenantCode}/admins/create`}>
            Add Admin
          </Link>
          <Link className="bg-white shadow px-4 py-2 rounded text-sm font-semibold" to={`/su/admin/tenants/${tenantCode}/config`}>
            Edit Config
          </Link>
          <Link className="bg-white shadow px-4 py-2 rounded text-sm font-semibold" to={`/su/admin/tenants/${tenantCode}/firebase`}>
            Firebase Configuration
          </Link>
          <Link className="bg-primary text-white shadow px-4 py-2 rounded text-sm font-semibold" to={`/su/admin/tenants/${tenantCode}/edit`}>
            Edit Tenant
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded shadow p-6">
          <h2 className="uppercase text-sm font-bold text-slate-600 mb-4">
            Tenant
          </h2>
          <Info label="Code" value={tenant.code} />
          <Info label="Name" value={tenant.name} />
          <Info label="Status" value={tenant.active ? "Active" : "Inactive"} />
        </div>
        <div className="bg-white rounded shadow p-6 lg:col-span-2">
          <h2 className="uppercase text-sm font-bold text-slate-600 mb-4">
            Config
          </h2>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(tenant.config ?? {}).map(([key, value]) => (
              <div key={key}>
                <dt className="text-xs uppercase font-bold text-slate-500">
                  {key}
                </dt>
                <dd className="text-sm text-slate-700 break-all">
                  {value || "-"}
                </dd>
              </div>
            ))}
          </dl>
        </div>
        <div className="bg-white rounded shadow p-6 lg:col-span-3">
          <h2 className="uppercase text-sm font-bold text-slate-600 mb-4">
            Modules
          </h2>

          {moduleError ? (
            <div
              role="alert"
              className="bg-errorBg text-errorText text-sm p-4 mb-4 border-errorBorder border-[0.5px] rounded-sm"
            >
              {moduleError}
            </div>
          ) : null}
          {moduleSuccess ? (
            <div
              role="status"
              className="mb-4 rounded-sm border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700"
            >
              {moduleSuccess}
            </div>
          ) : null}

          {modulesLoading ? (
            <LoadingIcon />
          ) : (
            <div className="divide-y divide-slate-200">
              {modules.map((module) => {
                const pending = pendingModules[module.key] === true;
                return (
                  <div
                    className="flex items-center justify-between gap-4 py-4"
                    key={module.key}
                  >
                    <div>
                      <div className="text-sm font-semibold text-slate-700">
                        {module.name}
                      </div>
                      <div className="text-xs text-slate-500">{module.key}</div>
                      {moduleDescriptions[module.key] ? (
                        <p className="mt-1 max-w-3xl text-xs text-slate-500">
                          {moduleDescriptions[module.key]}
                        </p>
                      ) : null}
                    </div>
                    <label className="relative inline-flex cursor-pointer items-center">
                      <input
                        type="checkbox"
                        role="switch"
                        aria-label={`Enable ${module.name}`}
                        checked={module.enabled}
                        disabled={pending}
                        onChange={() => handleModuleToggle(module)}
                        className="peer sr-only"
                      />
                      <span className="h-6 w-11 rounded-full bg-slate-300 transition-colors peer-checked:bg-primary peer-disabled:cursor-wait peer-disabled:opacity-60 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-transform after:content-[''] peer-checked:after:translate-x-full" />
                    </label>
                  </div>
                );
              })}
            </div>
          )}

          {!modulesLoading && advancedPushModule ? (
            <div
              className={`mt-5 rounded border p-4 text-sm ${
                firebaseReady
                  ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                  : "border-amber-200 bg-amber-50 text-amber-800"
              }`}
            >
              <div className="font-semibold">
                {firebaseReady
                  ? "Firebase is ready for advanced delivery."
                  : "Firebase must be configured before this tenant can send advanced notifications."}
              </div>
              <p className="mt-1">
                Firebase readiness does not prevent enabling the module.
              </p>
              {!firebaseReady ? (
                <Link
                  className="mt-2 inline-block font-semibold underline"
                  to={`/su/admin/tenants/${tenantCode}/firebase`}
                >
                  Configure Firebase
                </Link>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      <ConfirmDialog
        title="Disable Advanced Push Notifications?"
        visible={Boolean(confirmingDisable)}
        onAccept={() =>
          confirmingDisable ? updateModule(confirmingDisable) : null
        }
        onDismiss={() => setConfirmingDisable(null)}
      >
        <div className="text-sm text-slate-700">
          <p className="font-semibold">
            Disabling pauses advanced push access; it does not cancel or delete
            notification data.
          </p>
          <ul className="mt-4 list-disc space-y-2 pl-5">
            <li>Advanced admin and mobile APIs become unavailable.</li>
            <li>New worker claims for this tenant are paused.</li>
            <li>An already claimed delivery batch may finish.</li>
            <li>Queued work can resume after re-enabling.</li>
            <li>
              Jobs, installations, preferences, images, and engagement history
              are retained.
            </li>
            <li>Legacy topic notifications remain unaffected.</li>
          </ul>
        </div>
      </ConfirmDialog>
    </div>
  );
};

const Info = ({ label, value }) => (
  <div className="mb-3">
    <div className="text-xs uppercase font-bold text-slate-500">{label}</div>
    <div className="text-sm text-slate-700">{value}</div>
  </div>
);

export default TenantDetail;
